import { Component, Prop, h, Host, State, forceUpdate } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";

interface HotSpotNavItem {
    number: number;
    name: string;
    nameHtml?: string;
    tabId: string;
}

@Component({
    tag: "ux-iws-hot-spot-nav",
    styleUrl: "hot-spot-nav.scss",
    shadow: {
        delegatesFocus: true,
    },
})
export class HotSpotNav {
    private tabContainer: HTMLUxTabContainerElement;

    /** show numbers instead of bullet points for each nav item */
    @Prop() showNumbers: boolean = false;

    /** id of ux-tab-container that contains the targets */
    @Prop() tabContainerId?: string = "";

    /** show a scroll-to-top-button below the nav items */
    @Prop() showScrollToTop: boolean = false;

    @State() activeTabId: string = "";

    @State() nameWidth: number = 0;

    @State() navItems: HotSpotNavItem[] = [];

    private init() {
        this.tabContainer = (this.tabContainerId === ""
            ? document.querySelector("ux-tab-container")
            : document.getElementById(
                  this.tabContainerId
              )) as HTMLUxTabContainerElement;
        this.tabContainer?.addEventListener("uxActiveTabSet", (event) => {
            this.activeTabId = event.detail;
        });

        const tabs = this.tabContainer?.querySelectorAll("ux-tab") || [];
        if (tabs.length <= 0) return;
        this.navItems = Array.from(tabs).map((tab, index) => {
            const { tabId } = tab;
            const nameAttribute = tab.getAttribute("name");
            const nameSlot = tab.querySelector(`[slot="label"]`);
            return {
                number: index + 1,
                name: nameAttribute || nameSlot?.textContent || "",
                nameHtml: nameSlot?.innerHTML,
                tabId,
            };
        });
    }

    private onClick = (tabId: string) => {
        this.tabContainer?.setActiveTab(tabId, true);
    };

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentDidLoad() {
        this.init();
    }

    render() {
        const { t } = i18nProvider;
        return (
            <Host>
                <div
                    class="drawer"
                    style={{
                        width: `${this.nameWidth + 46}px`,
                    }}
                >
                    {this.navItems.map((item) => {
                        return (
                            <button
                                type="button"
                                class={{
                                    item: true,
                                    "item--active":
                                        this.activeTabId === item.tabId,
                                }}
                                onClick={() => this.onClick(item.tabId)}
                                key={item.tabId}
                            >
                                <span class="item__indicator">
                                    {this.showNumbers ? (
                                        <span class="item__number">
                                            {item.number}
                                        </span>
                                    ) : (
                                        <span class="item__bullet"></span>
                                    )}
                                </span>
                                <span
                                    ref={({ offsetWidth }) => {
                                        if (offsetWidth > this.nameWidth) {
                                            this.nameWidth = offsetWidth;
                                        }
                                    }}
                                    class="item__name"
                                    key={item.name}
                                    innerHTML={item.nameHtml}
                                >
                                    {(item.nameHtml === null ||
                                        item.nameHtml === "" ||
                                        typeof item.nameHtml === "undefined") &&
                                        item.name}
                                </span>
                            </button>
                        );
                    })}
                    {this.showScrollToTop && (
                        <button
                            type="button"
                            class="item item--scroll-to-top"
                            onClick={() =>
                                window.scroll({ top: 0, behavior: "smooth" })
                            }
                            key="scroll-to-top"
                        >
                            <span class="scroll-to-top-icon">
                                <ux-icon
                                    name="chevron-up"
                                    weight="bold"
                                ></ux-icon>
                            </span>
                            <span
                                ref={(el) => {
                                    const offsetWidth = el?.offsetWidth;
                                    if (offsetWidth > this.nameWidth) {
                                        this.nameWidth = offsetWidth;
                                    }
                                }}
                                class="item__name"
                            >
                                {t("scrollToTop.label", "Scroll to top")}
                            </span>
                        </button>
                    )}
                </div>
            </Host>
        );
    }
}
